import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./UniversalProductModal.module.css";
import { FiX, FiType, FiDelete, FiBarChart2 } from "react-icons/fi";
import { API_URL } from "../../config/api";

const sanitizeMoneyInput = (val) => {
  const v = String(val ?? "").replace(",", ".");
  if (!/^\d*\.?\d*$/.test(v)) return null;
  return v;
};

const normalizeMoney = (val) => {
  if (val === "" || val === ".") return 0;
  const n = Number(val);
  return Number.isFinite(n) ? n : 0;
};

const UniversalProductModal = ({ open, onClose, onSave }) => {
  const [rows, setRows] = useState([{ priceStr: "0.00", barcode: "", name: "" }]);
  const [activeIndex, setActiveIndex] = useState(0);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const activeRow = rows[activeIndex] || rows[0];

  useEffect(() => {
    if (!open) return;
    setRows([{ priceStr: "0.00", barcode: "", name: "" }]);
    setActiveIndex(0);
    setSaving(false);
    setError("");
  }, [open]);

  const totalCount = useMemo(() => rows.length, [rows]);

  const setActiveField = (field, value) => {
    setRows((prev) => prev.map((r, i) => (i === activeIndex ? { ...r, [field]: value } : r)));
  };

  const handlePriceChange = (next) => {
    const cleaned = sanitizeMoneyInput(next);
    if (cleaned === null) return;
    setActiveField("priceStr", cleaned);
  };

  const handleKeypad = (key) => {
    let next = activeRow?.priceStr ?? "0.00";

    if ((next === "0.00" || next === "0") && key !== ".") next = "";

    if (key === ".") {
      if (next.includes(".")) return;
      next = next.length ? next + "." : "0.";
      handlePriceChange(next);
      return;
    }

    if (key === "bksp") {
      if (!next || next.length === 0) return handlePriceChange("");
      if (next.length === 1) return handlePriceChange("");
      return handlePriceChange(next.slice(0, -1));
    }

    handlePriceChange(next + String(key));
  };

  const addMore = () => {
    setRows((prev) => [...prev, { priceStr: "0.00", barcode: "", name: "" }]);
    setActiveIndex((prev) => prev + 1);
  };

  // ✅ POST universal product
  const createUniversalProduct = async ({ name_ru, ntin, sell_price }) => {
    const token = localStorage.getItem("token");

    const payload = {
      type: "universal",
      // ✅ если name_ru пустой — бек сам подставит (как у тебя "Универсальный товар 5")
      name_ru: name_ru ?? "",
      ntin: ntin ?? "",
      measure_unit_id: 1,
      sell_price: Number(sell_price ?? 0),
    };

    const res = await fetch(`${API_URL}/api/products`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    });

    const text = await res.text();

    let data = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      data = { message: text };
    }

    if (!res.ok) {
      throw new Error(data?.message || data?.error || `Ошибка создания товара (HTTP ${res.status})`);
    }

    return data; // ожидаем { data: {...} }
  };

  const handleSave = async () => {
    if (saving) return;
    setError("");

    const prepared = rows
      .map((r) => ({
        price: normalizeMoney(r.priceStr),
        barcode: (r.barcode || "").trim(),
        name: (r.name || "").trim(),
      }))
      .filter((r) => r.price > 0 || r.barcode || r.name);

    if (prepared.length === 0) {
      alert("Заполните сумму или название/штрихкод");
      return;
    }

    const badPrice = prepared.find((r) => !(Number(r.price) > 0));
    if (badPrice) {
      alert("Укажите сумму больше 0");
      return;
    }

    try {
      setSaving(true);

      // ✅ создаём товары и возвращаем наверх уже ГОТОВЫЕ объекты для корзины
      const createdForCart = [];

      for (let i = 0; i < prepared.length; i++) {
        const r = prepared[i];

        const resp = await createUniversalProduct({
          name_ru: r.name,        // может быть пустым
          ntin: r.barcode,
          sell_price: r.price,
        });

        const api = resp?.data || resp || {};

        // ✅ если пользователь не ввёл имя — берём name_ru от API
        const finalName = (r.name || "").trim() || api.name_ru || "Универсальный товар";

        // ✅ id берём из API (реальный id товара)
        const finalId = api.id;

        if (!finalId) {
          throw new Error("API не вернул id товара");
        }

        createdForCart.push({
          id: finalId, // ✅ главное: реальный ID
          name: finalName,
          price: Number(api.sell_price ?? r.price ?? 0),
          qty: 1,
          quantity: 999999999,
          unit: api.measure_unit_id ?? null,
          ntin: api.ntin ?? r.barcode ?? "",
          isUniversal: true,
          api, // оставим на всякий (дебаг)
        });
      }

      onSave?.(createdForCart);
      onClose?.();
    } catch (e) {
      console.error("create universal product error:", e);
      setError(e?.message || "Ошибка при создании универсального товара");
      alert(e?.message || "Ошибка при создании универсального товара");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className={styles.overlay}
      onMouseDown={() => {
        if (!saving) onClose?.();
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape" && !saving) onClose?.();
      }}
      tabIndex={-1}
    >
      <div className={styles.modal} onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        {/* HEADER */}
        <div className={styles.header}>
          <div className={styles.title}>Универсальный товар</div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={() => {
              if (!saving) onClose?.();
            }}
            aria-label="Закрыть"
            disabled={saving}
          >
            <FiX size={18} />
          </button>
        </div>

        {/* PRICE */}
        <div className={styles.field}>
          <div className={styles.fieldBox}>
            <input
              className={styles.priceInput}
              value={activeRow?.priceStr ?? ""}
              onChange={(e) => handlePriceChange(e.target.value)}
              onFocus={(e) => e.target.select()}
              inputMode="decimal"
              placeholder="0.00"
              disabled={saving}
            />
            <button type="button" className={styles.iconBtn} title="Тип" onClick={() => {}} disabled={saving}>
              <FiType size={18} />
            </button>
          </div>
        </div>

        {/* BARCODE */}
        <div className={styles.field}>
          <div className={styles.fieldBox}>
            <input
              className={styles.textInput}
              value={activeRow?.barcode ?? ""}
              onChange={(e) => setActiveField("barcode", e.target.value)}
              placeholder="Штрихкод (не обязательно)"
              disabled={saving}
            />
            <div className={styles.iconStatic} aria-hidden="true" title="Штрихкод">
              <FiBarChart2 size={18} />
            </div>
          </div>
        </div>

        {/* NAME */}
        <div className={styles.field}>
          <div className={styles.fieldBox}>
            <input
              className={styles.textInput}
              value={activeRow?.name ?? ""}
              onChange={(e) => setActiveField("name", e.target.value)}
              placeholder="Название (не обязательно)"
              disabled={saving}
            />
          </div>
        </div>

        {/* KEYPAD */}
        <div className={styles.keypad}>
          {["1", "2", "3", "4", "5", "6", "7", "8", "9", ".", "0"].map((k) => (
            <button key={k} type="button" className={styles.keyBtn} onClick={() => handleKeypad(k)} disabled={saving}>
              {k}
            </button>
          ))}

          <button
            type="button"
            className={`${styles.keyBtn} ${styles.keyBtnBackspace}`}
            onClick={() => handleKeypad("bksp")}
            title="Удалить"
            disabled={saving}
          >
            <FiDelete size={18} />
          </button>
        </div>

        {/* ACTIONS */}
        <div className={styles.actionsRow}>
          <button type="button" className={styles.addMoreBtn} onClick={addMore} disabled={saving}>
            + Добавить ещё
          </button>

          <button type="button" className={styles.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? "Сохранение..." : "Сохранить"}
          </button>
        </div>

        <button type="button" className={styles.cancelBtn} onClick={() => !saving && onClose?.()} disabled={saving}>
          Отменить
        </button>

        {!!error && <div style={{ marginTop: 10, color: "#e53935", fontSize: 12, textAlign: "center" }}>{error}</div>}

        <div className={styles.hint}>
          <span>
            Строк: <strong>{totalCount}</strong>
          </span>
          <span className={styles.dot}>•</span>
          <span>
            Активная: <strong>{activeIndex + 1}</strong>
          </span>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default UniversalProductModal;
